import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import CookieJs from "js-cookie";
import get from "lodash/get";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import employeeService from "../../services/employee";
import employeePerformanceService from "../../services/employeePerformance";
import queryDefault from "../../utils/queryDefault";

const monthOptions = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const getYearOptions = () => {
  const thisYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, index) => thisYear - index);
};

const toNumber = (value: string | number) => Number(value || 0);

export default function CreateEmployeePerformance() {
  const user = JSON.parse(CookieJs.get("USER") || "{}");
  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();
  const notification = useNotification();
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      employee_id: "",
      month: currentMonth,
      year: currentYear,
      head_region: "",
      sales_achieve: 0,
      peer_crew: 0,
      head_region_score: 0,
      mystery_shopper: 0,
      monthly_assessment: 0,
      late_attendance: 0,
      late_report: 0,
      teguran: 0,
      complaint: 0,
    },
  });

  const listEmployees = useQuery(
    ["employee-options-create"],
    () => employeeService.getEmployee({ keyword: "" }),
    {
      ...queryDefault,
    }
  );

  const createEmployeePerformance = useMutation(
    "create-employee-performance",
    employeePerformanceService.createEmployeePerformance,
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Employee performance record created successfully",
          type: "success",
          position: "top",
        });
        navigate("/app/employee-performance");
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const onSubmit = (values: any) => {
    createEmployeePerformance.mutate({
      employee_id: toNumber(values.employee_id),
      month: toNumber(values.month),
      year: toNumber(values.year),
      head_region: values.head_region,
      sales_achieve: toNumber(values.sales_achieve),
      peer_crew: toNumber(values.peer_crew),
      head_region_score: toNumber(values.head_region_score),
      mystery_shopper: toNumber(values.mystery_shopper),
      monthly_assessment: toNumber(values.monthly_assessment),
      late_attendance: toNumber(values.late_attendance),
      late_report: toNumber(values.late_report),
      teguran: toNumber(values.teguran),
      complaint: toNumber(values.complaint),
      created_by: user.username,
    });
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Create Employee Performance</title>
      </Helmet>

      <Breadcrumb
        label="Create Employee Performance"
        breadcrumbs={[
          { label: "Employee Performance", href: "/app/employee-performance" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[4, 5]}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <FormLabel required>Employee</FormLabel>
                <Controller
                  name="employee_id"
                  control={form.control}
                  render={({ field }) => (
                    <Select {...field} displayEmpty>
                      <MenuItem value="">Select Employee</MenuItem>
                      {get(listEmployees, "data.data", []).map((employee: any) => (
                        <MenuItem key={employee.id} value={employee.id}>
                          {employee.name || employee.employee_name || employee.username}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <FormLabel required>Month</FormLabel>
                <Controller
                  name="month"
                  control={form.control}
                  render={({ field }) => (
                    <Select {...field}>
                      {monthOptions.map((month) => (
                        <MenuItem key={month.value} value={month.value}>
                          {month.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <FormLabel required>Year</FormLabel>
                <Controller
                  name="year"
                  control={form.control}
                  render={({ field }) => (
                    <Select {...field}>
                      {getYearOptions().map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Head Region</FormLabel>
                <Controller
                  name="head_region"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Sales Achieve</FormLabel>
                <Controller
                  name="sales_achieve"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Peer Crew</FormLabel>
                <Controller
                  name="peer_crew"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Head Region Score</FormLabel>
                <Controller
                  name="head_region_score"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Mystery Shopper</FormLabel>
                <Controller
                  name="mystery_shopper"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Monthly Assessment</FormLabel>
                <Controller
                  name="monthly_assessment"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Late Attendance</FormLabel>
                <Controller
                  name="late_attendance"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Late Report</FormLabel>
                <Controller
                  name="late_report"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Teguran</FormLabel>
                <Controller
                  name="teguran"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <FormLabel>Complaint</FormLabel>
                <Controller
                  name="complaint"
                  control={form.control}
                  render={({ field }) => <TextField {...field} size="small" type="number" />}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
            <LoadingButton
              variant="contained"
              loading={createEmployeePerformance.isLoading}
              onClick={form.handleSubmit(onSubmit)}
            >
              Save
            </LoadingButton>
            <Button variant="outlined" onClick={() => navigate("/app/employee-performance")}>
              Cancel
            </Button>
          </Stack>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
